import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  viewChild,
  effect,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';
import { IconComponent } from '@fc-shared/ui/icon/icon.component';
import { CoreInputModule } from '@fc-shared/ui/input/core/core-input.module';
import { FocusOnAppearModule } from '@fc-shared/directives/focus-on-appear/focus-on-appear.module';
import { QuickSearchFacadeService } from '../services/quick-search-facade.service';
import { NgButtonComponent } from '@fc-shared/ui/buttons/ng-button';

@Component({
  selector: 'fc-quick-search-input',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <fc-form-field>
      <fc-icon
        icon="icon-search-outline"
        fcPrefix
        [size]="20"
        class="text-secondary-300"
      />
      <input
        #inputEl
        placeholder="Search..."
        class="bg-white"
        fcInput
        [value]="facade.query()"
        (keydown)="facade.handleKeyDown($event)"
      />
      @if (facade.query()) {
        <ng-button
          variant="icon"
          icon="icon-close"
          fcSuffix
          (click)="facade.clearSearch(); $event.stopPropagation()"
        />
      }
    </fc-form-field>
  `,
  imports: [
    IconComponent,
    CoreInputModule,
    FocusOnAppearModule,
    NgButtonComponent,
  ],
})
export class QuickSearchInputComponent implements AfterViewInit {
  facade = inject(QuickSearchFacadeService);
  autoFocus = input(false);
  private inputEl = viewChild<ElementRef<HTMLInputElement>>('inputEl');
  private destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      const input = this.inputEl()?.nativeElement;
      if (!input) return;
      fromEvent<InputEvent>(input, 'input')
        .pipe(
          tap(() => this.facade.resetSelection()),
          debounceTime(180),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe((e) =>
          this.facade.setQuery((e.target as HTMLInputElement).value),
        );
    });
  }

  ngAfterViewInit(): void {
    if (this.autoFocus()) {
      this.focus();
    }
  }

  focus(): void {
    this.inputEl()?.nativeElement.focus();
  }
}
